import type { AdminCategoriesSelectDto, AdminCategorySelectDto, AdminCategoryUpdateDto } from '@@types/api.types';
import { AdminCategoriesSortField, AdminCategoriesSortOrder, AdminCategoriesTypeFilter } from '@@types/api.types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook';
import { queryKeys } from '@queries/keys';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetCurrentUser } from './users.query';

export type CategoriesQueryParams = {
  limit: number;
  order: AdminCategoriesSortOrder;
  page: number;
  search?: string;
  sort: AdminCategoriesSortField;
  type: AdminCategoriesTypeFilter;
  withoutSubcategories?: boolean;
};

export const useAdminCategoriesQuery = (params: CategoriesQueryParams) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminCategoriesSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminCategoriesSelectDto>({
        path: '/v1/admin/categories',
        query: params,
      });
    },
    queryKey: queryKeys.adminCategories.getCategories(params),
    placeholderData: keepPreviousData,
  });
};

export const useAdminCategoryQuery = (id: string | undefined, preloadedData?: AdminCategorySelectDto) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  const enabled = isAdmin && !!id;

  return useQuery<AdminCategorySelectDto, ApiErrorWrapper>({
    enabled,
    initialData: preloadedData,
    queryFn: async () => {
      return await apiGet<AdminCategorySelectDto>({
        path: `/v1/admin/categories/${id}`,
      });
    },
    queryKey: queryKeys.adminCategories.getCategoryById(id ?? ''),
  });
};

export const useAdminUpdateCategory = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminCategorySelectDto, ApiErrorWrapper, { dto: AdminCategoryUpdateDto; id: string }>({
    mutationFn: async (params) => {
      const { dto, id } = params;
      return await apiPatch<AdminCategorySelectDto>({
        body: dto,
        path: `/v1/admin/categories/${id}`,
      });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(queryKeys.adminCategories.getCategoryById(data.id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCategories.root });
    },
  });
};

export const useAdminUpdateCategoryStatus = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminCategorySelectDto, ApiErrorWrapper, { archived: boolean; id: string }>({
    mutationFn: async (params) => {
      const { archived, id } = params;
      return await apiPatch<AdminCategorySelectDto>({
        body: { archived },
        path: `/v1/admin/categories/${id}/status`,
      });
    },
    onSuccess: (data) => {
      void queryClient.setQueryData(queryKeys.adminCategories.getCategoryById(data.id), data);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCategories.root });
    },
  });
};

export const useAdminDeleteCategory = () => {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, ApiErrorWrapper, string>({
    mutationFn: async (id) => {
      return await apiDelete<{ success: boolean }>({
        path: `/v1/admin/categories/${id}`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCategories.getCategories() });
    },
  });
};

export const useAdminCreateCategory = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminCategorySelectDto, ApiErrorWrapper, AdminCategoryUpdateDto>({
    mutationFn: async (dto) => {
      return await apiPost<AdminCategorySelectDto>({
        body: dto,
        path: `/v1/admin/categories`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCategories.root });
    },
  });
};

export const useAdminAvailableParentCategoriesQuery = (categoryId?: string) => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminCategoriesSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminCategoriesSelectDto>({
        path: '/v1/admin/categories',
        query: {
          limit: 100,
          order: AdminCategoriesSortOrder.ASC,
          page: 1,
          sort: AdminCategoriesSortField.NAME,
          type: AdminCategoriesTypeFilter.MAIN,
        },
      });
    },
    queryKey: queryKeys.adminCategories.getAvailableParents(categoryId),
  });
};

export const useAdminAvailableSubcategoriesQuery = () => {
  const { apiGet } = useApi();
  const { isAdmin } = useGetCurrentUser();

  return useQuery<AdminCategoriesSelectDto, ApiErrorWrapper>({
    enabled: isAdmin,
    queryFn: async () => {
      return await apiGet<AdminCategoriesSelectDto>({
        path: '/v1/admin/categories',
        query: {
          limit: 100,
          order: AdminCategoriesSortOrder.ASC,
          page: 1,
          sort: AdminCategoriesSortField.NAME,
          type: AdminCategoriesTypeFilter.MAIN,
          withoutSubcategories: true,
        },
      });
    },
    queryKey: queryKeys.adminCategories.getAvailableSubcategories(),
  });
};
